import { get, merge } from 'lodash';
import { Question } from '../question';
import { Option } from '../option';
import { ReplyItem } from '../reply';
import { getAllLeafPaths } from '@schoolbelle/common/object-change-tracker';
import { Subject } from 'rxjs';

export type QuestionFilter = {
  [question_id: string]: { [option_id: string]: 'checked' | 'unchecked' };
};

export class RepliesFilter {
  private filters: QuestionFilter[] = [];
  /**
   * every action to change filter will stream change event.
   */
  private changeEvent = new Subject<QuestionFilter[]>();
  get onChange() {
    return this.changeEvent.asObservable();
  }
  toggleOptionFilter(
    option: Option,
    should: 'checked' | 'unchecked' = 'checked'
  ) {
    const filter = this.filters.find(f =>
      ['checked', 'unchecked'].includes(get(f, [option.question.id, option.id]))
    );
    const should_old = get(filter, [option.question.id, option.id]);

    /**
     * clear old filter
     */
    if (filter) this.filters.splice(this.filters.indexOf(filter), 1);
    if (should_old === undefined || should_old !== should) {
      /**
       * push new filter
       */
      this.filters.push({ [option.question.id]: { [option.id]: should } });
    }
    this.changeEvent.next(this.filters);
  }
  toggleQuestionFilter(question: Question, should: 'any' | 'none') {
    const optionShould = should === 'any' ? 'checked' : 'unchecked';
    const should_old = this.getQuestionShould(question);

    /**
     * clear all filters that deal with the question
     * */

    this.filters = this.filters.filter(filter => !get(filter, [question.id]));

    if (should_old === undefined || should_old !== should) {
      /**
       * push filters to say any of options should be checked ( q1.o1.checked or q1.o2.checked or ....)
       * */

      if (should === 'any')
        question.options.forEach(o =>
          this.filters.push({ [question.id]: { [o.id]: optionShould } })
        );
      /**
       * push one filter that says every option of the question should be unchecked.
       * _.assign does not work since it does not merge deep. use _.merge instead.
       *  */ else
        this.filters.push(
          merge(
            {},
            ...question.options.map(o => {
              return { [question.id]: { [o.id]: optionShould } };
            })
          )
        );
    }
    this.changeEvent.next(this.filters);
  }
  empty () {
    this.filters = [];
    this.changeEvent.next(this.filters);
  }

  /**
   *
   * @param option
   * get current 'should' state of a option in filter system
   */
  getOptionShould(option: Option): 'checked' | 'unchecked' | undefined {
    const relatedFilter = this.filters.find(f =>
      ['checked', 'unchecked'].includes(get(f, [option.question.id, option.id]))
    );
    if (relatedFilter)
      return get(relatedFilter, [option.question.id, option.id]);
    else return undefined;
  }
  /**
   *
   * @param question
   * get current 'should' status of a question in filter system
   * it is translated to a bit more readable form than {[option_id:string]:'checked'|'unchecked'}
   */
  getQuestionShould(question: Question): 'any' | 'none' | undefined {
    /**
     * find filter that says every option of the question should be unchecked.
     */
    const noneFilter = this.filters.find(filter => {
      return (
        question.options.length ===
        question.options.filter(
          o => get(filter, [question.id, o.id]) === 'unchecked'
        ).length
      );
    });
    if (noneFilter) return 'none';

    /**
     * fild all scattered filters that says a option of the question should be checked
     * and see all options of the question are mentioned like:
     * q1.o1 should be checked or q1.o2 should be checked or q1.o3 should be checked ....
     * */
    const checkedFilteredOptionLength = question.options.filter(o => {
      return this.filters.find(
        filter => get(filter, [o.question.id, o.id]) === 'checked'
      );
    }).length;
    const questionOptionLength = question.options.length;
    if (checkedFilteredOptionLength === questionOptionLength) return 'any';
    else return undefined;
  }
  /**
   *
   * @param reply
   * check reply against a series of filters to see if it passes.
   */
  checkReply(reply: ReplyItem) {
    if (!this.filters.length) return true;
    /**
     * find one filter that reply can pass.
     *  */ else
      return this.filters.find(filter => {
        const paths = getAllLeafPaths(filter);
        /**
         * every leaf of filter should be satisfied.
         */
        return (
          paths.length ===
          paths.filter(path => {
            const should = get(filter, path.split('.')) as
              | 'checked'
              | 'unchecked';
            if (should === 'checked')
              return typeof get(reply.snapshot, path) !== 'undefined';
            else return typeof get(reply.snapshot, path) === 'undefined';
          }).length
        );
      })
        ? true
        : false;
  }
}
